import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/common";
import { CheckCircle2, XCircle, Loader2, ArrowLeft } from "lucide-react";

export default function VaultOrderSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      if (attempts.current >= 7) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/vault/orders/status/${sessionId}`);
        if (["paid", "production", "quality_control", "delivered", "completed"].includes(data.status)) { setState("paid"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  const Icon = state === "paid" ? CheckCircle2 : state === "checking" ? Loader2 : XCircle;
  const title = { checking: "Confirming payment…", paid: "Payment confirmed", timeout: "Still processing", error: "Payment issue" }[state];
  const msg = {
    checking: "Verifying the transaction with Stripe.",
    paid: "Real payment recorded. Production has automatically started on the deliverable.",
    timeout: "Payment is taking a moment to confirm — it will appear in the Vault shortly.",
    error: "We couldn't confirm this payment. No revenue was recorded.",
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070a0f] p-6">
      <Card className="p-8 max-w-md w-full text-center" data-testid="vault-order-success-card">
        <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${state === "paid" ? "bg-emerald-500/15 text-emerald-400" : state === "checking" ? "bg-slate-800 text-slate-300" : "bg-red-500/15 text-red-400"}`}>
          <Icon className={`w-8 h-8 ${state === "checking" ? "animate-spin" : ""}`} />
        </div>
        <h1 className="font-head text-2xl font-bold text-slate-100 mt-5" data-testid="vault-order-status-title">{title}</h1>
        <p className="text-slate-400 mt-2 text-sm">{msg}</p>
        <button onClick={() => navigate("/vault")} data-testid="back-to-vault" className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to Vault
        </button>
      </Card>
    </div>
  );
}
